import React from 'react';
import PropTypes from 'prop-types';

import { v4 as uuid } from 'uuid';

import { Button } from 'react-bootstrap';

function StorageSize(props) {
    if (typeof props.value !== 'number') {
        return 'NaN';
    }
    if (props.value < 4 * 1024) {
        return props.value.toFixed(2);
    } else if (props.value < 4 * 1024 * 1024) {
        return `${(props.value / 1024).toFixed(2)} K`;
    } else if (props.value < 4 * 1024 * 1024 * 1024) {
        return `${(props.value / 1024 / 1024).toFixed(2)} M`;
    } else {
        return `${(props.value / 1024 / 1024 / 1024).toFixed(2)} G`;
    }
}

class DbStats extends React.PureComponent {
    state = {
        result: null,
    };

    componentDidMount() {
        this.fetchDbStats();
    }

    async fetchDbStats() {
        const response = await fetch('/api/dbstats');
        const result = await response.json();
        this.setState({ result: result.result });
    }

    render() {
        if (!this.state.result) {
            return "Loading...";
        }
        const data = this.state.result;
        return (
            <ul>
                <li>Collections: {data.collections}<a href="/mongo">more</a></li>
                <li>Objects: {data.objects}</li>
                <li>Average Object Size: <StorageSize value={data.avgObjSize} /></li>
                <li>Data Size: <StorageSize value={data.dataSize} /></li>
                <li>Storage Size: <StorageSize value={data.storageSize} /></li>
                <li>Indexes: {data.indexes}</li>
                <li>Index Size: <StorageSize value={data.indexSize} /></li>
                <li style={{ color: data.fsUsedSize > 0.8 * data.fsTotalSize ? 'red' : null }}>FileSystem Used: <StorageSize value={data.fsUsedSize} /></li>
                <li>FileSystem Total: <StorageSize value={data.fsTotalSize} /></li>
            </ul>
        );
    }
}

class TextWithHighlight extends React.PureComponent {
    onMouseOver = () => {
        if (this.props.text !== this.props.highlight && this.props.onChangeHighlight) {
            this.props.onChangeHighlight(this.props.text);
        }
    }
    onMouseOut = () => {
        if (this.props.text === this.props.highlight && this.props.onChangeHighlight) {
            this.props.onChangeHighlight();
        }
    }
    render() {
        return (<span onMouseOver={this.onMouseOver} onMouseOut={this.onMouseOut} style={{ backgroundColor: this.props.text === this.props.highlight ? 'yellow' : null }}>{this.props.text}</span>);
    }
}

TextWithHighlight.propTypes = {
    text: PropTypes.string.isRequired,
    highlight: PropTypes.string,
    onChangeHighlight: PropTypes.func,
};

class VersionInfo extends React.PureComponent {
    state = {
        loading: true,
        error: '',
        json: {},
    };

    componentDidMount() {
        fetch(this.constructUrl())
            .then(response => response.text())
            .then(text => {
                try {
                    const json = JSON.parse(text);
                    this.setState({ loading: false, json });
                } catch (err) {
                    const parts = text.substring(3).split(',').map(p => p.trim());
                    this.setState({ loading: false, json: { result: text, commit: parts[0], dataCommit: parts[1], serverDataCommit: parts[2] } });
                }
            })
            .catch(err => {
                if (err) {
                    console.error(err);
                    this.setState({ loading: false, error: err.toString() });
                } else {
                    this.setState({ loading: false, error: 'Unknown error' });
                }
            });
    }

    constructUrl() {
        const fields = ['version', 'platform'];
        const params = ["json=true"];
        for (let field of fields) {
            const value = this.props.client[field];
            if (value) {
                params.push(encodeURIComponent(field) + '=' + encodeURIComponent(value));
            }
        }
        if (params.length == 0) {
            return this.props.url;
        }
        return this.props.url + '?' + params.join('&');
    }

    render() {
        if (this.state.loading) {
            return <div>Loading...</div>;
        }
        if (this.state.error) {
            return <div>{this.state.error}</div>;
        }
        return (<div>
            <ul>
                <li>{this.state.json.result}</li>
                <li>Server: <a href={'https://gitlab.corp.dreamcraft.com/dreamcraft/wildsky-server/commits/' + this.state.json.commit} target="_blank" rel="noopener noreferrer">
                    <TextWithHighlight text={this.state.json.commit} highlight={this.props.highlight} onChangeHighlight={this.props.onChangeHighlight} />
                </a></li>
                <li>Data: <a href={'https://gitlab.corp.dreamcraft.com/dreamcraft/wildsky-data/commits/' + this.state.json.dataCommit} target="_blank" rel="noopener noreferrer">
                    <TextWithHighlight text={this.state.json.dataCommit} highlight={this.props.highlight} onChangeHighlight={this.props.onChangeHighlight} />
                </a></li>
                <li>PatchData: <a href={'https://gitlab.corp.dreamcraft.com/dreamcraft/wildsky-patch/commits/' + this.state.json.patchDataCommit} target="_blank" rel="noopener noreferrer">
                    <TextWithHighlight text={this.state.json.patchDataCommit} highlight={this.props.highlight} onChangeHighlight={this.props.onChangeHighlight} />
                </a></li>
                <li>ServerData: <a href={'https://gitlab.corp.dreamcraft.com/dreamcraft/wildsky-serverdata/commits/' + this.state.json.serverDataCommit} target="_blank" rel="noopener noreferrer">
                    <TextWithHighlight text={this.state.json.serverDataCommit} highlight={this.props.highlight} onChangeHighlight={this.props.onChangeHighlight} />
                </a></li>
            </ul>
        </div>);
    }
}

VersionInfo.propTypes = {
    url: PropTypes.string.isRequired,
    client: PropTypes.object.isRequired,
    highlight: PropTypes.string,
    onChangeHighlight: PropTypes.func,
};

class VersionTable extends React.PureComponent {
    constructor(props) {
        super(props);

        this.urls = [
            // "http://localhost:8080/health",
            "https://devtd.wildsky.dev/health",
            "https://td.wildsky.dev/health",
            "https://tdap.wildsky.dev/health",
            "https://tdec.wildsky.dev/health",
        ];

        if (window.location.protocol.startsWith("https")) {
            this.urls = this.urls.filter(url => url.startsWith("https"));
        }

        this.state = {
            clients: this.loadClients(),
            refreshKey: uuid(),
            highlight: '',
        };
    }

    loadClients() {
        const text = window.localStorage.getItem('VersionTable.clients');
        if (!text) {
            return [{ version: '', platform: '' }, { version: '1.0.33', platform: 'Android' }];
        }
        return JSON.parse(text);
    }

    saveClients(clients) {
        window.localStorage.setItem('VersionTable.clients', JSON.stringify(clients));
    }

    componentDidUpdate() {
        this.saveClients(this.state.clients);
    }

    handleClientField = (i, field) => e => {
        const clients = this.state.clients.slice();
        clients[i] = { ...this.state.clients[i], [field]: e.target.value };
        this.setState({ clients });
    }

    addClient = () => {
        this.setState({ clients: this.state.clients.concat([{ version: '', platform: '' }]) });
    }

    removeClient = i => () => {
        const clients = this.state.clients.slice(0, i).concat(this.state.clients.slice(i + 1));
        this.setState({ clients });
    }

    refresh = () => {
        this.setState({ refreshKey: uuid() });
    }

    onChangeHighlight = value => {
        this.setState({ highlight: value });
    }

    render() {
        return (<div>
            <Button onClick={this.addClient}>Add Client</Button>
            <Button onClick={this.refresh}>Refresh</Button>
            <table style={{ width: '100%' }}>
                <thead>
                    <tr>
                        <th>Client</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {
                        this.state.clients.map((client, i) => (
                            <tr key={i}>
                                <td>
                                    <table>
                                        <tbody>
                                            <tr><td>Version</td><td><input type="text" value={client.version} onChange={this.handleClientField(i, 'version')} /></td></tr>
                                            <tr>
                                                <td>Platform</td>
                                                <td>
                                                    <input type="text" value={client.platform} onChange={this.handleClientField(i, 'platform')} />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <Button onClick={this.removeClient(i)}>Remove</Button>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                        {
                                            this.urls.map(url => (
                                                <div key={url + client.version + client.platform + this.state.refreshKey} style={{ margin: '8px' }}>
                                                    <a href={url} target="_blank" rel="noopener noreferrer"><TextWithHighlight text={url} highlight={this.state.highlight} onChangeHighlight={this.onChangeHighlight} /></a>
                                                    <VersionInfo url={url} client={client} highlight={this.state.highlight} onChangeHighlight={this.onChangeHighlight} />
                                                </div>
                                            ))
                                        }
                                    </div>
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>
        </div>);
    }
}

export class AwsMonitor extends React.PureComponent {
    render() {
        return (<div>
            <div style={{ display: 'flex' }}>
                <h1>AWS</h1>
                <DbStats />
            </div>
            <VersionTable />
        </div>);
    }
}
